/* eslint-disable react/prop-types */
import PropTypes from 'prop-types';
import React, {
  useState,
  useEffect,
  useRef,
  useMemo
} from 'react';

import { useMutation, useLazyQuery }    from '@apollo/client';
import { Map, List, Set } from 'immutable';
import isEmpty            from 'lodash/isEmpty';
import ReactHtmlParser    from 'react-html-parser';
import recordJS           from 'records';

import Alert            from '@mui/material/Alert';
import AlertTitle       from '@mui/material/AlertTitle';
import Box              from '@mui/material/Box';
import Container        from '@mui/material/Container';
import LoadingButton    from '@mui/lab/LoadingButton';
import Grid             from '@mui/material/Grid';
import TextField        from '@mui/material/TextField';
import Typography       from '@mui/material/Typography';
import FormGroup        from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox         from '@mui/material/Checkbox';
import IntlTelInput     from 'components/Generic/IntlTelInput';

import { errorSelector } from 'helpers/static';
import { cleanName }     from 'helpers/string';
import applicationJourneyGtm from 'helpers/gtm';
import { initProfileAttributes } from 'helpers/candidate_attributes_generator';

import { CANDIDATE_APPLICATION_APPLY } from 'queries/candidate/apply';
import { CANDIDATE_REFERRER_FETCH } from 'queries/candidate/referrer/fetch';
import { CANDIDATE_APPLICATION_REFERRAL_CREATE } from 'queries/candidate/application_referral/create';

import Display               from 'components/Generic/Display';
import CommunicationChannels from 'components/LandingPage/CommunicationChannels';
import PrivacyPolicyPopUp    from '../PrivacyPolicyPopUp';
import Resume from './Resume';

function LandingPageApply(props) {
  const {
    uri, navigate, theme, querystring, source, campaign, company,
  } = props;

  const [state, setState] = useState(new Map());

  const stateRef = useRef();

  // Used in `setCandidateInput` because of an edge case when toggling the country code dropdown
  // it resets the `candidate` variable.
  stateRef.current = state;

  const searchParams = new URLSearchParams(window.location.search);

  const candidate = state.get('candidate', new Map({
    campaignId:        `${campaign.id}`,
    firstName:         '',
    lastName:          '',
    email:             '',
    msisdn:            '',
    source,
    profileAttributes: initProfileAttributes(searchParams),
    preferredCommunicationChannels: new List(),
  }));

  const persistentPrivacyPolicy = useMemo(() => {
    return company.parameters.privacyPolicyLink;
  }, [company.parameters.privacyPolicyLink]);

  const dataErrors      = state.get('dataErrors', new Map()); // return error from astraeus
  const dataValidations = state.get('dataValidations', new Map()); // return error from astraeus
  const errors          = state.get('errors', new Map()); // errors from UI validation
  const channelErrors   = state.get('channelErrors', new Set());
  const privacyPolicy   = persistentPrivacyPolicy && !company.get('landingPagePopUpScreen') || state.get('privacyPolicy', false);
  const isSubmittable   = state.get('isSubmittable', false);
  const whatsappOptin   = state.get('whatsappOptin', false);

  const [submitApplication, { loading, error, data }] = useMutation(CANDIDATE_APPLICATION_APPLY, { errorPolicy: 'all' });
  const [fetchReferrer] = useLazyQuery(CANDIDATE_REFERRER_FETCH);
  const [createApplicationReferral] = useMutation(CANDIDATE_APPLICATION_REFERRAL_CREATE);

  const CHANNEL_VALIDATIONS = {
    email:    'email',
    sms:      'msisdn',
    whatsapp: 'msisdn',
  };

  const CHANNEL_VALIDATIONS_COPIES = {
    msisdn: t('eos.landing_page.label.phone_number'),
    email:  t('eos.landing_page.label.email'),
  };

  const [referrer, setReferrer] = useState(null);
  const [referrerLoading, setReferrerLoading] = useState(false);
  const [referrerError, setReferrerError] = useState(false);

  const getReferrer = async (token) => {
    setReferrerLoading(true);
    setReferrerError(false);
    
    try {
      const result = await fetchReferrer({
        variables: { search: { where: [{ field: 'token', value: [token] }] } },
      });

      if (result.data?.candidate?.referrers?.collection?.length > 0) {
        setReferrer(result.data.candidate.referrers.collection[0]);
      } else {
        setReferrerError(true);
      }
    } catch (error) {
      console.error('Failed to fetch referrer:', error);
      setReferrerError(true);
    } finally {
      setReferrerLoading(false);
    }
  };

  useEffect(() => {
    const referrerToken = searchParams?.get('referrer_token');

    if (!referrerToken) return;

    getReferrer(referrerToken);
  }, [searchParams?.get('referrer_token')]);

  useEffect(() => {
    if (!referrer
      || state.getIn(['candidate', 'profileAttributes', 'erp_referrer_name'])
      || state.getIn(['candidate', 'profileAttributes', 'erp_referrer_email'])) {
      return;
    }

    const firstName = referrer.firstName?.trim() || '';
    const lastName = referrer.lastName?.trim() || '';
    const fullName = `${firstName} ${lastName}`.trim();
    const email = referrer.email?.trim() || '';

    if (!fullName && !email) {
      console.warn('Referrer data incomplete - no name or email available:', referrer);
      return;
    }

    const profileAttributes = [...candidate.get('profileAttributes').toJS()];
    
    profileAttributes.push({ key: 'erp_referrer_name', value: fullName || email });

    if (email) {
      profileAttributes.push({ key: 'erp_referrer_email', value: email });
    }

    if (company.erpCustomFieldsEnabled() && referrer.customFields) {
      Object.entries(referrer.customFields).forEach(([key, value]) => {
        if (value && value.toString().trim()) {
          profileAttributes.push({
            key,
            value: value.toString().trim(),
          });
        }
      });
    }

    if (fullName || email) {
      setState(
        state.set('candidate',
          candidate.set('profileAttributes', new List(profileAttributes))
            .set('source', 'Employee Referral Program')),
      );
    }
  }, [referrer]);

  useEffect(() => {
    const noErrors = errors.filter((err) => err.length > 0).size === 0;

    let channelErrorsValues = new Set();
    let whatsappOptInValue = false;

    const channels = candidate.get('preferredCommunicationChannels');
    if (channels.size) {
      const errorsToShow = [];
      channels.forEach(
        (channel) => {
          if (CHANNEL_VALIDATIONS[channel] && !candidate.get(CHANNEL_VALIDATIONS[channel])) {
            errorsToShow.push(CHANNEL_VALIDATIONS[channel]);
          }
        },
      );

      channelErrorsValues = new Set(errorsToShow);
      whatsappOptInValue = channels.includes('whatsapp');
    }

    const contactDetailsPresent = company.getIn(['parameters', 'mandatoryContactDetails'])
      ? (!isEmpty(candidate.get('email')) && !isEmpty(candidate.get('msisdn')) && candidate.get('msisdn').length > 7)
      : (!isEmpty(candidate.get('email')) || !isEmpty(candidate.get('msisdn')));
    
    const nameDetailsPresent = !isEmpty(candidate.get('firstName')) && !isEmpty(candidate.get('lastName'));

    if (
      noErrors
      && nameDetailsPresent
      && contactDetailsPresent
      && privacyPolicy
      && channelErrorsValues.size === 0
    ) {
      setState(state.set('isSubmittable', true)
        .set('whatsappOptin', whatsappOptInValue)
        .set('channelErrors', channelErrorsValues));
    } else {
      setState(state.set('isSubmittable', false)
        .set('whatsappOptin', whatsappOptInValue)
        .set('channelErrors', channelErrorsValues));
    }
  }, [candidate, privacyPolicy]);

  useEffect(() => {
    const profileAttributes = candidate.get('profileAttributes', new List());
    const indexOfWhatsapp = profileAttributes.findIndex((p) => p.key === 'whatsapp_optin');

    if (indexOfWhatsapp >= 0 && profileAttributes.getIn([indexOfWhatsapp, 'value']) !== whatsappOptin) {
      const optInValue = whatsappOptin ? 'Yes' : 'No';
      setState(state.set('candidate', candidate.set('profileAttributes', profileAttributes.setIn([indexOfWhatsapp, 'value'], optInValue))));
    }
  }, [whatsappOptin]);

  useEffect(() => {
    if (data) {
      const record = recordJS({ data });
      const applicant = record.getIn(['data', 'candidate', 'applications', 'applications', 0]);

      const recordErrors = record.getIn(['data', 'candidate', 'applications', 'errors'], new Map()) || record.getIn(['data', 'candidate', 'applications', 'applications', 'errors'], new Map());
      const recordValidations = record.getIn(['data', 'candidate', 'applications', 'validations'], new Map()) || record.getIn(['data', 'candidate', 'applications', 'applications', 'validations'], new Map());

      if (typeof recordValidations === 'object' && recordValidations.size > 0) {
        setState(state.set('dataValidations', recordValidations));
        return;
      }

      if (typeof recordErrors === 'object' && recordErrors.size > 0) {
        setState(state.set('dataErrors', recordErrors));
        return;
      }

      if (referrer && company.get('erpEnabled')) {
        createApplicationReferral({
          variables: {
            application_referrals: [
              {
                applicationId: applicant.id,
                referrerId:    referrer.id,
              },
            ],
          },
        });
      }

      // had to remove /apply causing some errors
      if (applicant && applicant.bearerToken.length > 0) {
        // Fire GTM event for apply start
        applicationJourneyGtm(campaign.id).setAppId(applicant.id);

        let candidateLocation = `${uri.replace('/apply', '')}/candidate/${applicant.bearerToken}`;

        if (querystring.length > 0) {
          candidateLocation = `${candidateLocation}?${querystring}`;
        }

        // console.log('mutation', applicant.bearerToken, data, candidateLocation);
        if (candidate.get('preferredCommunicationChannels').includes('messenger')) {
          // Redirect to messenger url
          window.open(`${campaign.get('messengerUrl')}?ref=optin.${applicant.id}`);
        }
        navigate(candidateLocation);
      }
    }
  }, [data]);

  useEffect(() => {
    if (error) {
      setState(state.set('dataErrors', new Map()).set('dataValidations', new Map()));
    }
  }, [error]);

  function setCandidateInput(element) {
    setState(state.set('candidate', stateRef.current.get('candidate').set(element.target.name, element.target.value))
      .set('errors', errors.set(element.target.name, errorSelector(element)))
      .set('privacyPolicy', stateRef.current.get('privacyPolicy')));
  }

  function setPrivacyPolicy() {
    setState(state.set('privacyPolicy', !state.get('privacyPolicy')));
  }

  function onSubmit() {
    const applicant = {
      firstName:         cleanName(candidate.get('firstName')),
      lastName:          cleanName(candidate.get('lastName')),
      campaignId:        candidate.get('campaignId'),
      source:            candidate.get('source'),
      profileAttributes: candidate.get('profileAttributes').toJS(),
      insights:          candidate.get('insights', {}),
      education:         candidate.get('education', null),
    };

    if (!isEmpty(candidate.get('email'))) {
      applicant.email = candidate.get('email');
    }

    if (!isEmpty(candidate.get('msisdn'))) {
      applicant.msisdn = candidate.get('msisdn');
    }

    if (candidate.get('preferredCommunicationChannels').size) {
      applicant.preferredCommunicationChannels = candidate.get('preferredCommunicationChannels').toJS();
    }

    if (!isEmpty(candidate.get('curriculum'))) {
      applicant.curriculum = candidate.get('curriculum');
    }

    submitApplication({
      variables:
      {
        applications: [applicant],
      },
    });
  }

  function errorMessages() {
    let errs;
    // return error from apollo
    if (error) {
      errs = new List(error.graphQLErrors).map(
        (err) => (
          <li key={err}>
            {err.extensions ? err.extensions.problems[0].message : err.message}
          </li>
        ),
      );
    }

    if (dataErrors.size > 0) {
      errs = dataErrors.get('0').map((err) => <li key={err}>{err}</li>);
    }

    return (
      <ul style={{ padding: 0 }}>
        {errs}
      </ul>
    );
  }

  function validationMessages() {
    const messages = dataValidations.toList().map((map) => map.toList()).flatten();

    if (messages.size === 1) {
      return (<Typography>{messages.get(0)}</Typography>);
    }

    return (
      <ul style={{ margin: 0, paddingLeft: 16 }}>
        {messages.map((message, key) => <li key={key}><Typography>{message}</Typography></li>)}
      </ul>
    );
  }

  const onCommunicationChannelsChange = (channels) => {
    setState(state.set('candidate', candidate.set('preferredCommunicationChannels', new List(channels))));
  };

  const phoneNumberLabel = company.getIn(['parameters', 'mandatoryContactDetails'])
    ? t('eos.landing_page.label.phone_number') + ' *'
    : t('eos.landing_page.label.phone_number');

  function onProcessedResume(data) {
    const profileAttributes = candidate.get('profileAttributes', new List());
    const resumeKeys = new Set(data.profileAttributes.map((p) => p.key));

    profileAttributes.forEach((attr) => {
      if (!resumeKeys.has(attr.key)) {
        data.profileAttributes.push(attr);
      }
    });

    const msisdn = data.msisdn || candidate.get('msisdn');

    const newCandidate = stateRef.current.get('candidate')
      .set('curriculum', data.signedBlobId)
      .set('firstName', data.firstName)
      .set('lastName', data.lastName)
      .set('email', data.email)
      .set('msisdn', msisdn)
      .set('education', data.education)
      .set('profileAttributes', new List(data.profileAttributes))
      .set('insights', data.insights);

    setState(state.set('candidate', newCandidate));
  }

  if (error) console.error('error', error, 'graphQLErrors', error.graphQLErrors);

  const fieldStyle = {
    width:                    '100%',
    'input:-webkit-autofill': {
      boxShadow: '0 0 0 50px white inset',
    },
    'input:-webkit-autofill:focus': {
      boxShadow: '0 0 0 50px white inset',
    },
  };

  return (
    <Container>
      <Grid container>
        <Grid
          item
          xs={12}
          sx={{
            mt:                             4,
            mb:                             5,
            [theme.breakpoints.down('sm')]: {
              textAlign: 'center',
            },
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography
              variant="h1"
              sx={{
                fontWeight:                     'medium',
                fontSize:                       '1.5rem',
                [theme.breakpoints.down('sm')]: {
                  textAlign: 'center',
                  color:     'primary.main',
                },
              }}
            >
              {campaign.title}
            </Typography>

            <Display if={campaign.applyResume}>
              <Resume onProcessed={onProcessedResume} campaignId={candidate.get('campaignId')} />
            </Display>
          </Box>
        </Grid>

        <Grid container item spacing={2}>
          <Grid item md={6} xs={12}>
            <TextField
              required
              id="first-name"
              label={t('eos.landing_page.label.first_name')}
              name="firstName"
              error={errors.get('firstName') && errors.get('firstName').length > 0}
              value={candidate.get('firstName') || ''}
              onChange={(e) => setCandidateInput(e)}
              // helperText={errors.get('firstName') && errors.getIn(['firstName', 0])}
              disabled={loading === true}
              sx={fieldStyle}
            />
          </Grid>
          <Grid item md={6} xs={12}>
            <TextField
              required
              id="last-name"
              label={t('eos.landing_page.label.last_name')}
              name="lastName"
              error={errors.get('lastName') && errors.get('lastName').length > 0}
              value={candidate.get('lastName') || ''}
              onChange={(e) => setCandidateInput(e)}
              // helperText={errors.get('lastName') && errors.getIn(['lastName', 0])}
              disabled={loading === true}
              sx={fieldStyle}
            />
          </Grid>
          <Grid item md={6} xs={12}>
            <TextField
              required
              id="email"
              label={t('eos.landing_page.label.email')}
              name="email"
              error={errors.get('email') && errors.get('email').length > 0}
              value={candidate.get('email')}
              onChange={(e) => setCandidateInput(e)}
              // helperText={errors.get('email') && errors.getIn(['email', 0])}
              disabled={loading === true}
              type="email"
              sx={fieldStyle}
            />
          </Grid>
          <Grid item md={6} xs={12}>
            <IntlTelInput
              id="phone-number"
              name="msisdn"
              label={phoneNumberLabel}
              value={candidate.get('msisdn')}
              onChange={setCandidateInput}
              disabled={loading === true}
              initialCountry={campaign.countryShortCode}
            />
          </Grid>
        </Grid>

        <Grid item xs={12} sx={{ mt: 3 }}>
          <CommunicationChannels onChange={onCommunicationChannelsChange} company={company} />
          <Display if={channelErrors.size}>
            <Grid sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <ul key={error} style={{ padding: 0 }} data-testid="channel_errors_container">
                {
                  channelErrors.map(
                    (channelError) => (
                      <li key={channelError} style={{ padding: 0, color: 'red' }}>
                        {t('eos.landing_page.communication_channels.errors.field_required', { field: CHANNEL_VALIDATIONS_COPIES[channelError] })}
                      </li>
                    ),
                  )
                }
              </ul>
            </Grid>
          </Display>
        </Grid>

        <Display if={!persistentPrivacyPolicy && !candidate.get('preferredCommunicationChannels').includes('sms')}>
          <Grid item xs={12} sx={{ mt: 3 }}>
            <FormGroup>
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={privacyPolicy}
                    onChange={() => setPrivacyPolicy()}
                  />
                )}
                label={ReactHtmlParser(company.privacyPolicyText())}
                sx={{
                  a: {
                    color:          'primary.main',
                    textDecoration: 'none',
                    '&:hover':      {
                      textDecoration: 'underline',
                    },
                  },
                  [theme.breakpoints.only('xs')]: {
                    alignItems: 'start',
                  },
                }}
              />
            </FormGroup>
          </Grid>
        </Display>

        <Display
          if={
            !persistentPrivacyPolicy && candidate.get('preferredCommunicationChannels').includes('sms')
          }
        >
          <Grid item xs={12} sx={{ mt: 3 }}>
            <FormGroup>
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={privacyPolicy}
                    onChange={() => setPrivacyPolicy()}
                  />
                )}
                label={ReactHtmlParser(company.smsPrivacyPolicyText())}
                sx={{
                  a: {
                    color:          'primary.main',
                    textDecoration: 'none',
                    '&:hover':      {
                      textDecoration: 'underline',
                    },
                  },
                  [theme.breakpoints.only('xs')]: {
                    alignItems: 'start',
                  },
                }}
              />
            </FormGroup>
          </Grid>
        </Display>

        <Grid container sx={{ display: 'flex', justifyContent: 'center' }}>
          <Grid
            item
            md={6}
            xs={12}
            sx={{
              display: 'flex', mt: 4, justifyContent: 'center', flexDirection: 'column',
            }}
          >
            <Grid sx={{ display: 'flex', justifyContent: 'center' }}>
              <Display id="error" if={dataErrors.size > 0 || error}>
                <Alert severity="error">
                  <AlertTitle>{t('eos.landing_page.label.error')}</AlertTitle>
                  {errorMessages()}
                </Alert>
              </Display>
              <Display id="validations" if={dataValidations.size > 0}>
                <Alert severity="info">
                  {validationMessages()}
                </Alert>
              </Display>
            </Grid>
            {referrerLoading && (
              <Grid sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Typography 
                  variant="body2" 
                  sx={{ textAlign: 'center', color: 'info.main' }}
                >
                  {t('eos.landing_page.label.loading_referrer_info', { defaultValue: 'Loading referrer information...' })}
                </Typography>
              </Grid>
            )}
            {referrerError && (
              <Grid sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Alert severity="warning" sx={{ width: '100%' }}>
                  {t('eos.landing_page.label.referrer_not_found', { defaultValue: 'Referrer link is invalid or expired. You can still apply without referral benefits.' })}
                </Alert>
              </Grid>
            )}
            <Grid sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
              <LoadingButton 
                disabled={!isSubmittable || referrerLoading} 
                loading={loading} 
                disableElevation 
                variant="contained" 
                color="primary" 
                size="large" 
                onClick={() => onSubmit()}
              >
                {t('eos.landing_page.label.get_started')}
              </LoadingButton>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <PrivacyPolicyPopUp show={!privacyPolicy && !!company.get('landingPagePopUpScreen')} onConfirm={setPrivacyPolicy} content={company.get('landingPagePopUpScreen')} footer={company.popUpPrivacyPolicyText()} />
    </Container>
  );
}

LandingPageApply.propTypes = {
  navigate: PropTypes.func,
  uri:      PropTypes.string,
};

export default LandingPageApply;
